import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { GetMasterDataProductUomsDTO } from "@src/domain/dto/masterData";
import type { MasterDataProductUom } from "@src/domain/entities/masterData";
import { GetMasterDataProductUomsUseCase } from "@src/application/useCases/masterData";
import { MasterDataProductUomRepository } from "@src/infrastructure/repositories/masterData";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  getMasterDataListCacheEntry,
  masterDataAllListCacheKey,
  setMasterDataListCacheEntry,
} from "./masterData/masterDataListCache";

interface MasterDataProductUomsDataContextValue {
  brandKey: string;
  productUoms: MasterDataProductUom[];
  count: number;
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  refetch: () => void;
}

const MasterDataProductUomsDataContext =
  createContext<MasterDataProductUomsDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataProductUoms(): MasterDataProductUomsDataContextValue {
  const context = useContext(MasterDataProductUomsDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataProductUoms must be used within MasterDataProductUomsDataProvider",
    );
  }
  return context;
}

interface MasterDataProductUomsDataProviderProps {
  brandKey: string;
  children: ReactNode;
}

/**
 * Loads every product UOM for the brand ({ items, count }, no pagination).
 * Tenant DB is selected via X-Brand-Key on MasterDataGraphQlAdapter.
 */
export function MasterDataProductUomsDataProvider({
  brandKey,
  children,
}: Readonly<MasterDataProductUomsDataProviderProps>) {
  const getProductUomsUseCase = useMemo(
    () => new GetMasterDataProductUomsUseCase(new MasterDataProductUomRepository()),
    [],
  );

  const initialCacheKey = masterDataAllListCacheKey("product-uoms", brandKey, "");
  const initialCached =
    getMasterDataListCacheEntry<MasterDataProductUom>(initialCacheKey);

  const [productUoms, setProductUoms] = useState<MasterDataProductUom[]>(
    () => initialCached?.items ?? [],
  );
  const [count, setCount] = useState(initialCached?.total ?? 0);
  const [loading, setLoading] = useState(!initialCached);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const lastReloadTokenRef = useRef(0);

  const refetch = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    MasterDataGraphQlAdapter.getInstance().setBrandKey(brandKey);
    return () => {
      MasterDataGraphQlAdapter.getInstance().setBrandKey(null);
    };
  }, [brandKey]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = masterDataAllListCacheKey("product-uoms", brandKey, search);
    const forceRefresh = reloadToken !== lastReloadTokenRef.current;
    const cached = getMasterDataListCacheEntry<MasterDataProductUom>(cacheKey);

    if (cached && !forceRefresh) {
      setProductUoms(cached.items);
      setCount(cached.total);
      setLoading(false);
      setError(null);
      return;
    }

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await getProductUomsUseCase.execute(
          new GetMasterDataProductUomsDTO({
            search: search.trim() || null,
          }),
        );
        if (cancelled) return;
        setMasterDataListCacheEntry(cacheKey, {
          items: result.items,
          total: result.count,
          page: 1,
          limit: result.count,
        });
        lastReloadTokenRef.current = reloadToken;
        setProductUoms(result.items);
        setCount(result.count);
      } catch (err) {
        if (cancelled) return;
        setProductUoms([]);
        setCount(0);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load master data product UOMs",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [brandKey, getProductUomsUseCase, reloadToken, search]);

  const value = useMemo(
    (): MasterDataProductUomsDataContextValue => ({
      brandKey,
      productUoms,
      count,
      loading,
      error,
      search,
      setSearch,
      refetch,
    }),
    [brandKey, productUoms, count, loading, error, search, setSearch, refetch],
  );

  return (
    <MasterDataProductUomsDataContext.Provider value={value}>
      {children}
    </MasterDataProductUomsDataContext.Provider>
  );
}
